<template>
  <div class="card-editor">
    <GeneralModal @close="onClosePageModal">
      <div class="header">
        <div
          class="round-button"
          @click="onShowPagePreview"
          title="Vorschau"
          v-b-tooltip.hover.top
        >
          <img :src="require('@/assets/img/eye-icon.svg')" />
        </div>
        <div
          class="round-button"
          @click="toggleSetting(true)"
          title="settings"
          v-b-tooltip.hover.top
        >
          <i class="fas fa-cog"></i>
        </div>
      </div>

      <div
        class="page-buider-wrapper fancy-scroll"
        @scroll.passive="showScrollbar"
      >
        <PageBuilderComponent
          ref="pageBuilderComponentRef"
          :task="{ props: option.modalConfig }"
          :taskProps="option.modalConfig"
          @onContentChange="onContentChange"
          :getCustomWidgets="onGetCustomWidgets"
          :saveCustomWidgets="onSaveCustomWidgets"
          :margin="task.props.margin"
        ></PageBuilderComponent>
      </div>

      <!-- preview wrapper -->
      <div
        class="preview-wrapper"
        v-if="pagePreviewBlobURL || isPreviewLoading"
      >
        <div class="loading-container" v-if="isPreviewLoading">
          <LoadingSpinner
            loadingCaption="Vorschau wird geladen..."
            v-show="isPreviewLoading"
          />
        </div>
        <template v-if="!isPreviewLoading">
          <PagePreviewModal
            @onClose="toggleOffPreview"
            :url="pagePreviewBlobURL"
          />
        </template>
      </div>

      <!-- settings wrapper -->
      <div class="settings-modal-wrapper" v-if="isSettingsModalVisible">
        <PagebuilderSettingsModal
          :task="task"
          @onClose="toggleSetting(false)"
          :isUserHasAccess="isUserHasAccess"
        />
      </div>
    </GeneralModal>
  </div>
</template>

<script>
import { PageBuilderComponent } from '@codelos.io/page-builder';
import GeneralModal from '@/components/modals/GeneralModal';
import LoadingSpinner from '@/components/widgets/LoadingSpinner.vue';
import PagePreviewModal from '@/components/modals/PagePreviewModal.vue';
import PagebuilderSettingsModal from '@/components/PageBuilderSettingsModal.vue';

import {
  saveCustomWidgets,
  getCustomWidgets,
} from '@/utils/helpers/PageBuilderHelper.js';
import { debouncer } from '@/utils/helpers';
import { generatePdf } from '@/services/workflow.service';
export default {
  name: 'card-editor',
  data() {
    return {
      isFullScreenPage: false,
      savedScrollPosition: 0,
      isSettingsModalVisible: false,
      isPreviewLoading: false,
      pagePreviewBlobURL: '',
      task: { props: { margin: 'small-margin' } },
    };
  },
  props: {
    option: {
      type: Object,
      required: true,
    },
    isUserHasAccess: {
      type: Boolean,
      default: true,
    },
  },

  methods: {
    onContentChange(event) {
      this.option.modalConfig.jsonContent = event.jsonContent;
      this.option.modalConfig.htmlContent = event.htmlContent;
    },
    onClosePageModal() {
      setTimeout(() => {
        window.scrollTo(0, this.savedScrollPosition);
      });
      this.$emit('close');
    },
    async onGetCustomWidgets() {
      const widgets = await getCustomWidgets();
      return widgets;
    },

    async onSaveCustomWidgets(widgetElement) {
      try {
        await saveCustomWidgets(widgetElement);
      } catch (error) {
        throw new Error(error);
      }
    },
    showScrollbar(event) {
      event.target.classList.add('show-scrollbar');
      debouncer(
        () => {
          event.target.classList.remove('show-scrollbar');
        },
        300,
        this,
        'removeScrollClass'
      );
    },
    getPagePayloadContent() {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.option.modalConfig.htmlContent;
      const htmlElements = tempDiv.querySelectorAll('.is-empty');
      htmlElements.forEach((element) => {
        element.classList.remove('is-empty');
      });
      return tempDiv.innerHTML;
    },
    async onShowPagePreview() {
      try {
        this.isPreviewLoading = true;

        const payload = {
          content: this.getPagePayloadContent(),
          values: this.getAPIPayloadInputs,
          margin: this.task.props.margin,
          htmlOnly: true,
        };
        const res = await generatePdf(payload);
        const blob = new Blob([res.data.data], { type: 'text/html' });
        this.pagePreviewBlobURL = URL.createObjectURL(blob);
      } catch (error) {
        console.log('error', error);
      } finally {
        this.isPreviewLoading = false;
      }
    },
    toggleOffPreview() {
      this.isPreviewLoading = false;
      this.revokePageURL();
    },
    revokePageURL() {
      if (this.pagePreviewBlobURL) {
        URL.revokeObjectURL(this.pagePreviewBlobURL);
        this.pagePreviewBlobURL = null;
      }
    },
    toggleSetting(isVisible = true) {
      this.isSettingsModalVisible = isVisible;
      this.$set(this.option.modalConfig, 'margin', this.task.props.margin);
    },
  },
  created() {
    if (this.option.modalConfig?.jsonContent) {
      this.option.modalConfig?.jsonContent?.children?.forEach((pageNode) => {
        if (pageNode.type === 'table' && !('boldRows' in pageNode.value)) {
          pageNode.value['boldRows'] = new Array(pageNode.value.rows).fill(
            false
          );
        }
      });
    }
    this.task.props.margin = this.option?.modalConfig?.margin || 'small-margin';
  },
  mounted() {
    this.$nextTick(() => {
      this.option.modalConfig.jsonContent =
        this.option.modalConfig.jsonContent ||
        this.$refs.pageBuilderComponentRef?.pageBuilderNodes;
      this.option.modalConfig.htmlContent =
        this.option.modalConfig.htmlContent ||
        this.$refs.pageBuilderComponentRef.getSanitizedString();
    });
  },
  beforeDestroy() {
    this.revokePageURL();
  },
  components: {
    PageBuilderComponent,
    GeneralModal,
    PagePreviewModal,
    LoadingSpinner,
    PagebuilderSettingsModal,
  },
};
</script>

<style scoped>
.card-editor {
  background-color: #ffffff;
  position: relative;
  z-index: 999;
}

.card-editor ::v-deep .general-modal-wrapper .general-modal-close-icon {
  z-index: 1;
}
.card-editor .header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 40px 0 20px;
}
.card-editor .header .round-button {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  font-size: 18px;
  color: #2c3e50;
  cursor: pointer;
  border: 1px solid #d2d2d2;
}

.card-editor ::v-deep .general-modal-content {
  padding: 25px 20px 10px 0px;
}
.card-editor ::v-deep .general-modal-wrapper .general-modal-body {
  overflow: unset;
}

.card-editor ::v-deep .general-modal-wrapper .general-modal-content-wrapper {
  width: 93%;
  height: 91%;
}

.card-editor .page-buider-wrapper {
  height: calc(100% - 120px);
  overflow: auto;
}

/* overwrite pagebuilder css */
.card-editor .page-buider-wrapper ::v-deep .codelos-page-content {
  padding-top: 0 !important;
  padding-bottom: 10px !important;
  text-align: left;
  font-size: 16px;
  color: #212529;
}
.card-editor .page-buider-wrapper ::v-deep .codelos-page-content * {
  box-sizing: border-box !important;
}
.card-editor .page-buider-wrapper ::v-deep .codelos-html-wrapper {
  min-height: 50px;
}

.card-editor .page-buider-wrapper ::v-deep .element-wrapper .add-element-icon {
  padding-top: 1px !important;
}

.card-editor
  .page-buider-wrapper
  ::v-deep
  .codelos-hr-block
  .element-wrapper
  .draggible-icon
  svg {
  top: 1px;
}
.card-editor
  .page-buider-wrapper
  ::v-deep
  .codelos-text-block
  .element-wrapper
  .draggible-icon
  svg {
  top: 11px;
}
.card-editor .page-buider-wrapper ::v-deep .element-block {
  line-height: 1.5;
}

.card-editor .page-buider-wrapper ::v-deep .modal-backdrop.custom-backdrop {
  background: transparent;
}
.card-editor .page-buider-wrapper ::v-deep .codelos-dropdown-child,
.card-editor .page-buider-wrapper ::v-deep .codelos-dropdown {
  box-sizing: border-box;
}
.card-editor .page-buider-wrapper ::v-deep .codelos-dropdown-item {
  line-height: 1.5;
}

.card-editor
  .page-buider-wrapper
  ::v-deep
  .codelos-table-block
  .table-wrap
  tr
  td
  input {
  font-size: 16px;
  color: #212529;
}
.card-editor
  .page-buider-wrapper
  ::v-deep
  .codelos-table-block
  .codelos-table-header
  td
  input {
  font-weight: 600;
  font-size: 18px;
  color: #212529;
}
.card-editor
  .page-buider-wrapper
  ::v-deep
  .codelos-table-block
  .table-centered
  .table-wrap
  table {
  box-sizing: border-box;
}
.card-editor
  .page-buider-wrapper
  ::v-deep
  .codelos-text-block
  .element-wrapper
  .draggible-icon {
  top: -12px;
}

.card-editor .page-buider-wrappe ::v-deep .column-selector,
.card-editor .page-buider-wrappe ::v-deep .row-selector {
  box-shadow: unset;
  padding: 0;
}

.card-editor .preview-wrapper {
  position: absolute;
  border-radius: 20px;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: #ffffff;
  z-index: 12;
}
.card-editor .preview-wrapper .loading-container {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 11;
}
.card-editor .preview-wrapper .loading-container .loading {
  border-radius: 20px;
}

.card-editor .settings-modal-wrapper {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 20;
  height: 100%;
}
</style>
